import { ASSESSMENT_TYPE } from '@web-platform/shared-universal-constants/src/booking/assessmentType';

import type {
  SkyscannerCommonV1Money,
  SkyscannerSonarCommonV1Baggage,
  SkyscannerSonarCommonV1Enums,
  SkyscannerSonarV3Response,
} from '@skyscanner-internal/sonar-web-http-client';
import type { AssessmentType } from '@web-platform/shared-universal-constants/src/booking/assessmentType';
import type { FareAttributeTypes } from '@web-platform/shared-universal-constants/src/fareAttributes';
import type { Money } from '@web-platform/shared-universal-constants/src/money';
import type {
  PricingOptionFare,
  PricingOptionFareAttribute,
  PricingOptionFareBaggageFare,
} from '@web-platform/shared-universal-types/src/redux/state/entities/flights/pricingOption';

const ADDED_ATTRIBUTE_STATUSES: Partial<
  Record<SkyscannerSonarCommonV1Enums.Attribute, FareAttributeTypes>
> = {
  ATTRIBUTE_CABIN_BAGGAGE: 'CABIN_BAGGAGE',
  ATTRIBUTE_CHECKED_BAGGAGE: 'CHECKED_BAGGAGE',
};

const mapAttributeLabelStatuses = (
  addedAttributeLabelStatus: SkyscannerSonarCommonV1Enums.Attribute | undefined,
): FareAttributeTypes | undefined =>
  addedAttributeLabelStatus && ADDED_ATTRIBUTE_STATUSES[addedAttributeLabelStatus];

const mapFareAttributeAssessment = (
  assessment: SkyscannerSonarCommonV1Enums.Assessment | undefined,
): AssessmentType | undefined => assessment && ASSESSMENT_TYPE[assessment];

const mapAddedAttributeLabel = (
  addedAttributeLabel: SkyscannerSonarCommonV1Enums.Attribute[] | undefined,
): FareAttributeTypes[] | undefined =>
  addedAttributeLabel &&
  addedAttributeLabel.map((attribute) => mapAttributeLabelStatuses(attribute)).filter(Boolean);

const mapBaggageFee = (fee: SkyscannerCommonV1Money.Money | undefined): Money => ({
  amount: fee?.amount,
  currency_code: fee?.currencyCode,
  unit: fee?.unit,
});

const mapBaggage = (
  baggage: SkyscannerSonarCommonV1Baggage.Baggage | undefined,
): PricingOptionFareBaggageFare | undefined => {
  if (!baggage) return undefined;
  return {
    assessment: mapFareAttributeAssessment(baggage.assessment),
    fee: mapBaggageFee(baggage.fee),
    pieces: Number(baggage.pieces),
    weight: baggage.weight,
  };
};

export type PersonalItemAssessmentOutcome = 'INCLUDED' | 'UNAVAILABLE' | 'UNKNOWN';

const PERSONAL_ITEM_ASSESSMENT: Record<PersonalItemAssessmentOutcome, AssessmentType> = {
  INCLUDED: ASSESSMENT_TYPE.ASSESSMENT_INCLUDED,
  UNAVAILABLE: ASSESSMENT_TYPE.ASSESSMENT_UNAVAILABLE,
  UNKNOWN: ASSESSMENT_TYPE.ASSESSMENT_UNSPECIFIED,
};

const mapPersonalItem = (outcome: PersonalItemAssessmentOutcome): PricingOptionFareAttribute => ({
  assessment: PERSONAL_ITEM_ASSESSMENT[outcome],
});

export const mapSonarV3PricingOptionFare = (
  fare: SkyscannerSonarV3Response.Fare | undefined,
  personalItemOutcome: PersonalItemAssessmentOutcome = 'INCLUDED',
): PricingOptionFare => {
  if (!fare || (typeof fare === 'object' && Object.keys(fare).length === 0)) {
    return { personal_item: mapPersonalItem(personalItemOutcome) };
  }

  return {
    cabin_baggage: mapBaggage(fare?.cabinBaggage),
    checked_baggage: mapBaggage(fare?.checkedBaggage),
    added_attribute_labels: mapAddedAttributeLabel(fare?.addedAttributeLabels),
    brand_names: fare?.brandNames,
    advance_change: { assessment: mapFareAttributeAssessment(fare?.advanceChange?.assessment) },
    cancellation: { assessment: mapFareAttributeAssessment(fare?.cancellation?.assessment) },
    seat_pre_reservation: {
      assessment: mapFareAttributeAssessment(fare?.seatPreReservation?.assessment),
    },
    personal_item: mapPersonalItem(personalItemOutcome),
  };
};
